import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Trash2, Plus, Minus, Package, X } from 'lucide-react';
import { formatPrice } from '../lib/currency';
import { getCurrencyByLocale } from '../lib/currency';
import ParticleBackground from '../components/ParticleBackground';
import emailjs from '@emailjs/browser';
import { getProducts, convertToLegacyProduct, getProductsForCart } from '../lib/supabase';
import { useCartContext } from '../context/CartContext';
import { generateOrderNumber, formatOrderDate, formatOrderTime } from '../lib/orderUtils';
import OrderSuccessModal from '../components/OrderSuccessModal';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  category: string;
  vatIncluded: boolean;
  vatToggle: boolean;
}

interface ShippingOption {
  id: string;
  name: string;
  nameEn: string;
  nameDe: string;
  nameSk: string;
  description: string;
  descriptionEn: string;
  descriptionDe: string;
  descriptionSk: string;
  price: number;
  deliveryTime: string;
  deliveryTimeEn: string;
  deliveryTimeDe: string;
  deliveryTimeSk: string;
}

export default function CartPage() {
  const { t, i18n } = useTranslation();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState<string>('agreement');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [completedOrderNumber, setCompletedOrderNumber] = useState('');
  const { removeFromCart: removeFromCartDB, updateQuantity: updateQuantityDB, clearCart: clearCartDB } = useCartContext();
  const [vatToggles, setVatToggles] = useState<Record<string, boolean>>({});
  const [pendingUpdates, setPendingUpdates] = useState<Record<string, NodeJS.Timeout>>({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    billingAddress: '',
    shippingAddress: '',
    gateWidth: '',
    gateHeight: '',
    workHours: '',
    notes: '',
    consent: false,
  });

  const shippingOptions: ShippingOption[] = [
    {
      id: 'agreement',
      name: 'Cena dohodou',
      nameEn: 'Price by agreement',
      nameDe: 'Preis nach Vereinbarung',
      nameSk: 'Cena dohodou',
      description: 'Cena dopravy bude dohodnuta individuálně na základě vzdálenosti a způsobu dopravy. Kontaktujeme vás po odeslání objednávky.',
      descriptionEn: 'Shipping price will be agreed individually based on distance and shipping method. We will contact you after submitting the order.',
      descriptionDe: 'Der Versandpreis wird individuell nach Entfernung und Versandart vereinbart. Wir kontaktieren Sie nach der Bestellung.',
      descriptionSk: 'Cena dopravy bude dohodnutá individuálne na základe vzdialenosti a spôsobu dopravy. Kontaktujeme vás po odoslaní objednávky.',
      price: 0,
      deliveryTime: 'Dle dohody',
      deliveryTimeEn: 'By agreement',
      deliveryTimeDe: 'Nach Vereinbarung',
      deliveryTimeSk: 'Podľa dohody'
    },
    {
      id: 'pickup',
      name: 'Vlastní (dojedu si sám)',
      nameEn: 'Own (I will pick it up myself)',
      nameDe: 'Selbstabholung',
      nameSk: 'Vlastná (dojedem si sám)',
      description: 'Osobní vyzvednutí zboží na adrese výrobce. Adresa a čas vyzvednutí budou dohodnuty telefonicky nebo emailem.',
      descriptionEn: 'Personal pickup at the manufacturer\'s address. Address and pickup time will be agreed by phone or email.',
      descriptionDe: 'Persönliche Abholung an der Adresse des Herstellers. Adresse und Abholzeit werden telefonisch oder per E-Mail vereinbart.',
      descriptionSk: 'Osobné vyzdvihnutie tovaru na adrese výrobcu. Adresa a čas vyzdvihnutia budú dohodnuté telefonicky alebo emailom.',
      price: 0,
      deliveryTime: 'Dle dohody',
      deliveryTimeEn: 'By agreement',
      deliveryTimeDe: 'Nach Vereinbarung',
      deliveryTimeSk: 'Podľa dohody'
    }
  ];

  useEffect(() => {
    const loadCartItems = async () => {
      try {
        // Load cart from localStorage
        const cart = JSON.parse(localStorage.getItem('cart') || '{}');

        console.log('Loading cart from localStorage:', cart);
        console.log('Cart keys:', Object.keys(cart));

        // Load all products
        const rawProducts = await getProductsForCart().catch(() => []);
        const allProducts = rawProducts.map(convertToLegacyProduct);

        console.log('Raw products:', rawProducts.length);
        console.log('All products after conversion:', allProducts.length);
        console.log('First product:', allProducts[0]);
        console.log('Cart to load:', cart);

        const items: CartItem[] = [];

        // Convert cart to items array
        Object.entries(cart).forEach(([productId, quantity]) => {
          console.log('Looking for product:', productId);
          const product = allProducts.find((p: any) => String(p.id) === String(productId));
          console.log('Found product:', product);
          if (product && typeof quantity === 'number' && quantity > 0) {
            // Get product name based on language
            const currentLang = i18n.language === 'cz' ? 'cs' : i18n.language;
            let productName = '';

            // Check if product has name object (legacy format from convertToLegacyProduct)
            if (product.name && typeof product.name === 'object') {
              productName = product.name[currentLang] || product.name.cs || product.name['cs'] || 'Product';
            } else if (product.name_cs) {
              // Direct Supabase format
              if (currentLang === 'cs') {
                productName = product.name_cs;
              } else if (currentLang === 'en') {
                productName = product.name_en || product.name_cs;
              } else if (currentLang === 'de') {
                productName = product.name_de || product.name_cs;
              } else if (currentLang === 'sk') {
                productName = product.name_sk || product.name_cs;
              } else {
                productName = product.name_cs;
              }
            } else {
              productName = 'Product';
            }

            const finalPrice = product.discount && product.discount > 0
              ? parseFloat(product.price) * (1 - product.discount / 100)
              : parseFloat(product.price);

            items.push({
              id: productId,
              name: productName,
              price: finalPrice,
              quantity: quantity,
              image: product.image || '/-ct_logo_light copy copy.png',
              category: product.category,
              vatIncluded: product.vatIncluded || false,
              vatToggle: product.vatIncluded || false
            });
          }
        });

        console.log('Loaded cart items:', items);

        setCartItems(items);
      } catch (error) {
        console.error('Error loading cart items:', error);
        setCartItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCartItems();

    // Cleanup function to clear all pending timeouts
    return () => {
      Object.values(pendingUpdates).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
    };
  }, [i18n.language, pendingUpdates]);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      // Reload cart items when cart is updated
      const loadCartItems = async () => {
        try {
          const cart = JSON.parse(localStorage.getItem('cart') || '{}');

          const rawProducts = await getProductsForCart().catch(() => []);
          const allProducts = rawProducts.map(convertToLegacyProduct);

          const items: CartItem[] = [];
          Object.entries(cart).forEach(([productId, quantity]) => {
            const product = allProducts.find((p: any) => p.id === productId);
            if (product && typeof quantity === 'number' && quantity > 0) {
              // Get product name based on language
              const currentLang = i18n.language === 'cz' ? 'cs' : i18n.language;
              let productName = '';

              // Check if product has name object (legacy format from convertToLegacyProduct)
              if (product.name && typeof product.name === 'object') {
                productName = product.name[currentLang] || product.name.cs || product.name['cs'] || 'Produkt';
              } else if (product.name_cs) {
                // Direct Supabase format
                if (currentLang === 'cs') {
                  productName = product.name_cs;
                } else if (currentLang === 'en') {
                  productName = product.name_en || product.name_cs;
                } else if (currentLang === 'de') {
                  productName = product.name_de || product.name_cs;
                } else if (currentLang === 'sk') {
                  productName = product.name_sk || product.name_cs;
                } else {
                  productName = product.name_cs;
                }
              } else {
                productName = 'Produkt';
              }

              const finalPrice = product.discount && product.discount > 0
                ? parseFloat(product.price) * (1 - product.discount / 100)
                : parseFloat(product.price);

              items.push({
                id: productId,
                name: productName,
                price: finalPrice,
                quantity: quantity,
                image: product.image || '/-ct_logo_light copy copy.png',
                category: product.category,
                vatIncluded: product.vatIncluded || false,
                vatToggle: product.vatIncluded || false
              });
            }
          });

          setCartItems(items);
        } catch (error) {
          console.error('Error reloading cart:', error);
        }
      };

      loadCartItems();
    };

    // Listen for cart update events
    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('globalCartUpdate', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('globalCartUpdate', handleCartUpdate);
    };
  }, [i18n.language]);

  const updateQuantity = (id: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(id);
      return;
    }

    // Update UI immediately for responsiveness
    const updatedItems = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(updatedItems);

    // Update localStorage immediately
    const cart = JSON.parse(localStorage.getItem('cart') || '{}');
    cart[id] = newQuantity;
    localStorage.setItem('cart', JSON.stringify(cart));

    // Trigger cart update event immediately
    window.dispatchEvent(new Event('cartUpdated'));

    // Debounce database update
    if (pendingUpdates[id]) {
      clearTimeout(pendingUpdates[id]);
    }

    const timeoutId = setTimeout(async () => {
      await updateQuantityDB(id, newQuantity);
      setPendingUpdates(prev => {
        const newPending = { ...prev };
        delete newPending[id];
        return newPending;
      });
    }, 500);

    setPendingUpdates(prev => ({ ...prev, [id]: timeoutId }));
  };

  const removeItem = async (id: string) => {
    const updatedItems = cartItems.filter(item => item.id !== id);
    setCartItems(updatedItems);

    const cart = JSON.parse(localStorage.getItem('cart') || '{}');
    const cartCategories = JSON.parse(localStorage.getItem('cart_categories') || '{}');
    delete cart[id];
    delete cartCategories[id];
    localStorage.setItem('cart', JSON.stringify(cart));
    localStorage.setItem('cart_categories', JSON.stringify(cartCategories));

    // Remove from database
    await removeFromCartDB(id);

    // Trigger cart update event
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const toggleVat = (id: string) => {
    setVatToggles(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const VAT_RATE = 1.21;

  const isVatOn = (item: CartItem) => {
    if (item.category === 'unlimited') return false;
    return vatToggles[item.id] ?? item.vatIncluded;
  };

  const getItemPriceWithVat = (item: CartItem) => {
    return isVatOn(item) ? item.price * VAT_RATE : item.price;
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (getItemPriceWithVat(item) * item.quantity), 0);
  };

  const getShippingPriceInCZK = () => {
    const option = shippingOptions.find(opt => opt.id === selectedShipping);
    if (!option) return 0;
    return option.price;
  };

  const getShippingPrice = () => {
    const currency = getCurrencyByLocale(i18n.language);
    return Math.round(getShippingPriceInCZK() * currency.rate);
  };

  const formatShippingPrice = (priceInCZK: number) => {
    return formatPrice(priceInCZK, i18n.language);
  };

  const getFinalPrice = () => {
    return getTotalPrice() + getShippingPriceInCZK();
  };


  const handleOrderClick = () => {
    if (cartItems.length === 0) return;
    setShowForm(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedShipping(e.target.value);
  };

  const generateProductsHtml = () => {
    let html = `
      <table class="products" style="width: 100%; border-collapse: collapse; margin: 20px 0;">
        <thead>
          <tr style="background-color: #f5f5f5;">
            <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Produkt</th>
            <th style="border: 1px solid #ddd; padding: 12px; text-align: center;">Cena za kus</th>
            <th style="border: 1px solid #ddd; padding: 12px; text-align: center;">Množství</th>
            <th style="border: 1px solid #ddd; padding: 12px; text-align: right;">Celkem</th>
          </tr>
        </thead>
        <tbody>
    `;

    cartItems.forEach(item => {
      const basePrice = typeof item.price === 'number' ? item.price : parseFloat(item.price as any) || 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : parseInt(item.quantity as any) || 0;
      const price = getItemPriceWithVat(item);
      const totalItemPrice = price * quantity;
      const showVat = item.category !== 'unlimited';
      const vatLabel = showVat ? (isVatOn(item) ? 's DPH' : 'bez DPH') : '';

      html += `
        <tr>
          <td style="border: 1px solid #ddd; padding: 12px;">${item.name}${vatLabel ? ' <small>(' + vatLabel + ')</small>' : ''}</td>
          <td style="border: 1px solid #ddd; padding: 12px; text-align: center;">${formatPrice(price, i18n.language)}</td>
          <td style="border: 1px solid #ddd; padding: 12px; text-align: center;">${quantity}</td>
          <td style="border: 1px solid #ddd; padding: 12px; text-align: right; font-weight: bold;">${formatPrice(totalItemPrice, i18n.language)}</td>
        </tr>
      `;
    });

    html += `
        </tbody>
      </table>
    `;
    return html;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.consent) {
      alert(t('cart.consentRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      emailjs.init('saGOmdFFdZT_Ravmd');

      const orderNumber = await generateOrderNumber();
      const orderDate = formatOrderDate();
      const orderTime = formatOrderTime();
      const selectedShippingOption = shippingOptions.find(opt => opt.id === selectedShipping);

      // Parametry pro email zákazníkovi (potvrzení objednávky)
      const customerTemplateParams = {
        to_email: formData.email,
        order_number: orderNumber,
        order_date: orderDate,
        order_time: orderTime,
        customer_name: formData.name,
        customer_phone: formData.phone,
        billing_address: formData.billingAddress,
        shipping_address: formData.shippingAddress || formData.billingAddress,
        products: generateProductsHtml(),
        shipping: selectedShippingOption ?
          `${i18n.language === 'cz' || i18n.language === 'cs' ? selectedShippingOption.name :
            i18n.language === 'de' ? selectedShippingOption.nameDe :
            i18n.language === 'sk' ? selectedShippingOption.nameSk :
            selectedShippingOption.nameEn}` : '',
        subtotal: formatPrice(getTotalPrice(), i18n.language),
        shipping_cost: formatPrice(getShippingPriceInCZK(), i18n.language),
        total_price: formatPrice(getFinalPrice(), i18n.language)
      };

      // Parametry pro email majiteli (Borkovi)
      const ownerTemplateParams = {
        to_email: formData.email,
        order_number: orderNumber,
        order_date: orderDate,
        order_time: orderTime,
        customer_name: formData.name,
        customer_phone: formData.phone,
        from_name: formData.name,
        from_email: formData.email,
        reply_to: formData.email,
        phone: formData.phone,
        billing_address: formData.billingAddress,
        shipping_address: formData.shippingAddress || formData.billingAddress,
        door_width: formData.gateWidth,
        door_height: formData.gateHeight,
        workHours: formData.workHours,
        customer_notes: formData.notes,
        notes: formData.notes,
        products: generateProductsHtml(),
        shipping: selectedShippingOption ?
          `${i18n.language === 'cz' || i18n.language === 'cs' ? selectedShippingOption.name :
            i18n.language === 'de' ? selectedShippingOption.nameDe :
            i18n.language === 'sk' ? selectedShippingOption.nameSk :
            selectedShippingOption.nameEn} - ${formatPrice(getShippingPriceInCZK(), i18n.language)}` : '',
        subtotal: formatPrice(getTotalPrice(), i18n.language),
        shipping_cost: formatPrice(getShippingPriceInCZK(), i18n.language),
        total_price: formatPrice(getFinalPrice(), i18n.language)
      };

      // Odeslání emailů (chyba v emailech by neměla zrušit objednávku)
      try {
        // Odeslání emailu zákazníkovi (potvrzení objednávky)
        await emailjs.send(
          "service_pzo4xja",
          "template_xjz5k35",
          customerTemplateParams,
          "saGOmdFFdZT_Ravmd"
        );

        // Odeslání emailu majiteli (Borkovi)
        await emailjs.send(
          "service_azj90nv",
          "template_rwd9pir",
          ownerTemplateParams,
          "saGOmdFFdZT_Ravmd"
        );
      } catch (emailError) {
        console.error("Email sending error (order was saved):", emailError);
      }

      // Vyčistit košík
      setCartItems([]);
      localStorage.removeItem('cart');
      localStorage.removeItem('cart_categories');

      // Clear cart from database
      try {
        await clearCartDB();
      } catch (cartError) {
        console.error("Cart clearing error (order was saved):", cartError);
      }

      // Zobrazit success modal s animací
      setCompletedOrderNumber(orderNumber);
      setShowSuccessModal(true);
      setShowForm(false);
    } catch (error: any) {
      console.error("Order submission error:", error);

      // Zobrazit chybovou hlášku uživateli
      const errorDiv = document.createElement('div');
      errorDiv.className = 'fixed top-4 right-4 z-50 bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg';
      errorDiv.textContent = t('cart.orderError');
      document.body.appendChild(errorDiv);

      setTimeout(() => {
        errorDiv.remove();
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Funkce pro překlad podmínek
  const getTranslatedTerms = () => {
    if (i18n.language === 'cz' || i18n.language === 'cs') {
      return (
        <div className="space-y-4 text-sm leading-relaxed">
          <p><strong>I. Úvodní ustanovení</strong></p>
          <p>Obchodní podmínky
I. Úvodní ustanovení

1.

Tyto závazné obchodní podmínky platí pro nákup v internetovém obchodě  cybertronic-labs.cz . Tyto obchodní podmínky upravují vzájemný obchodní vztah mezi prodávajícím, kterým je provozovatel tohoto internetového obchodu a kupujícím (zákazníkem). Veškeré obchodní vztahy jsou uzavřeny v souladu s právním řádem České republiky. Je-li kupujícím spotřebitel, řídí se vztahy neupravené obchodními podmínkami zákonem č. 40/1964 Sb., občanským zákoníkem a zákonem č. 634/1992 Sb., zákonem o ochraně spotřebitele. Je-li kupujícím nikoli spotřebitel, řídí se vztahy neupravené obchodními podmínkami zákonem č. 513/1991 Sb., obchodním zákoníkem. Spotřebitel je osoba, která při uzavírání a plnění smlouvy nejedná v rámci své obchodní nebo jiné podnikatelské činnosti nebo osoba, která nakupuje výrobky nebo užívá služby za jiným účelem než pro podnikání s těmito výrobky nebo službami. Uzavřením kupní smlouvy kupující stvrzuje, že se seznámil s těmito obchodními podmínkami a že s nimi souhlasí. Na tyto obchodní podmínky je kupující dostatečným způsobem před vlastním uskutečněním objednávky upozorněn a má možnost se s nimi seznámit. Tyto níže uvedené podmínky platí pro internetový obchod zabývající se zakázkovým návrhem elektroniky René Letocha, CYBERTRONIC Unlimited, vedená v obchodním rejstříku, Na Chmelnici 1131/65, 696 04 SVATOBOŘICE-MISTŘÍN, IČ: 75685761, DIČ: (nejme plátci DPH). Tyto podmínky vymezují práva a povinnosti mezi prodávajícím a kupujícím

2.

Objednávka kupujícího je návrhem kupní smlouvy. Samotná kupní smlouva je uzavřena momentem závazného potvrzení návrhu smlouvy kupujícího prodávajícím. Smlouva je uzavírána v českém jazyce, nebrání-li tomu okolnosti na straně prodávajícího či kupujícího, lze smlouvu uzavřít i v jiném jazyce pro strany srozumitelném jazyce. Uzavřená smlouva je prodávajícím archivována za účelem jejího úspěšného splnění. Informace o jednotlivých technických krocích vedoucích k uzavření smlouvy jsou patrné z procesu objednávání v internetovém obchodě cybertronic-labs.cz, a také z těchto obchodních podmínek a kupující má možnost před vlastním odesláním objednávky zboží zkontrolovat případně opravit. Tyto obchodní podmínky jsou zobrazeny na webových stránkách internetového obchodu  cybertronic-labs.cz a je tak umožněna jejich archivace a reprodukce kupujícím. Objednávka zboží prostřednictvím internetového obchodu  cybertronic-labs.cz je považována za závaznou. Odesláním objednávky tímto kupující souhlasí, že se podrobně seznámil s obchodními podmínkami a reklamačním řádem a jejich obsah bez výhrad přijímá.


3.

Podmínkou uzavření elektronické objednávky (viz "Nákupní košík") je vyplnění veškerých registračních údajů dle registračního formuláře. Tímto kupující bez výhrad souhlasí s poskytnutím uvedených osobních dat a především s daným obsahem objednávky. Na osobní data poskytnutá kupujícím se vztahuje ochrana osobních dat (viz níže - oddíl IV.). Místem dodání zboží je adresa, jež kupující závazně udal v registračním formuláři (objednávce). V případě nutné změny je potřeba zcela zřetelně a to v co nejkratším časovém úseku informovat dodavatele o nutnosti změny daných dat (viz "Můj účet" – "Kontaktní údaje"). Tento krok je však bezpodmínečně nutno provést před obdržením samotného info e-mailu o vyexpedování dané zásilky na kupujícím uvedenou kontaktní adresu. Pokud kupující nepřevezme závazně objednané zboží na kontaktní adrese (viz "Registrační formulář") a řádně nezaplatí kupní cenu tohoto zboží v plné výši, má prodávající právo požadovat úhradu smluvní pokuty ve výši  40% z ceny objednaného zboží. Tím není dotčeno právo prodávajícího na náhradu škody převyšující smluvní pokutu z důvodu porušení uvedené povinnosti. Jestliže kupující nezaplatí dlužné částky ani po výzvě prodávajícího, resp. tuto výzvu nepřevezme, předá prodávající právní vymáhání veškerých pohledávek za kupujícím advokátní kanceláři, přičemž pohledávky se tak zvýší o náklady spojené s jejich vymáháním (odměna advokáta, soudní poplatek atp.).


4.

Vlastnické právo k danému zboží přechází na kupujícího úplným zaplacením kupní ceny (dle sazebníku cen).


5.

Prodávající se tímto zavazuje, že veškerá osobní data kupujícího poskytnutá jakoukoliv formou prodávajícímu jsou důvěrná a nebudou předána žádnému jinému subjektu. Tato data slouží pouze k samotné komunikaci mezi kupujícím a prodávajícím, případně pro zasílání informací tykajících se  cybertronic-labs.cz


III. Platební a dodací podmínky

1. Veškeré objednané skladové zboží včetně dokladu a návodu, nebo odkazem na návod je vyexpedováno podle momentální výrobní poptávky.
2. Platba od 1.12.2016 je pouze bankovním převodem. Platba v hotovosti ani dobírkou již není možná ani na vyžádání. S platbou vyčkejte na zaslání faktury se splatností, samotné potvrzení objednávky není faktura, variabilní symbol = číslo objendávky. V případě platby bez zaslání předfaktury, bude faktura přibalená u zaslaného zboží.

Bankovním převodem – platba na účet,

Česká republika: č.ú.: 1352505023/0800 ( ČESKÁ SPOŘITELNA ),

Zahraničí (IBAN): CZ30 0800 0000 0013 5250 5023,  BIC: GIBA CZ PX

 

Žádné zboží není držené fyzicky skladem, nejsme překupníci, ale výrobci zakázkových elektronikých stavebnic. Pokud je u vybraného produktu položka SKLADEM, znamená to, že je skladem pouze veškerý materiál nutný pro výrobu vybraného produktu, respektive je skladem produkt formou stavebnice. Výroba trvá v závislosti na vytíženosti v intervalu 1-20 pracovních dnů. Za standardních podmínek probíhá výroba do 10ti pracovních dnů. Dobu výroby a expedice lze urychlit (obvykle za příplatek). Více informací o rychlosti dodávky je možné konzultovat telefonicky, kde Vám budou poskytnuty veškeré informace. Veškeré další dotazy o stavu objednávky v tomto výrobním intervalu, nevedou k cíli a obvykle jen zdržují naše pracovníky se zbytečnou administrativní prací.


Celkovým nákupem se rozumí součet objednávek za jeden den odeslaných v jedné zásilce. V případě učinění více než jedné objednávky v jeden den, dochází k automatickému slučování objednávek a to z hlediska snížení nákladové položky manipulačního poplatku vůči koncovému spotřebiteli.

Ne veškeré nabízené zboží je skladem, respektive je skladem dostatečné množství komponentů pro jeho výrobu, respektive výrobek je skladem formou stavebnice. U neskladového zboží se automaticky prodlužují dodací lhůty až o 15 pracovních dní či až o 30 kalendářních dní dle jednotlivé nabídky vybraných produktů (veškeré informace o dostupnostech produktů jsou k dispozici přímo v detailu produktů). Kompletní nabídka skladového i neskladového zboží platí pouze a jen do vyprodání aktuálních zásob!! Pokud se u vybraného zboží objeví softwérový či hardwérový problém, je možné objednávku pozastavit po dobu odstranění problému.

Dále je nutné uvědomit si, že veškeré nabízené zboží je zhmotněním nápadu. Proto se vždy jedná o plně funkční prototyp, který je stále ve fázi vývoje tzv.UNFINISHED PROJECT. Pokud je zájem ze strany zákazníka o komerční využití, je nutné výrobek podrobit finálním testům ve zkušebnách pro zajištění dodržení norem (zákon č. 102/2001 Sb, zákon č. 22/1997 Sb, zákon č. 185/2001 Sb). Uváděné zboží na stránkách výrobce cybertronic-labs.cz je tedy plně funkční prototypový výrobek dodávaný formou stavebnice, který je laboratorně testovaný. Na jeho dotestování se podílí sám zákazník.

I když je našim cílem Vaše spokojenost a s tím související úplnost a pravdivost uvedených informací, popisů a fotografií,  může se stát,  že některé fotografie na těchto webových stránkách mohou být ilustrační. Jedná se zejména o položky, které jsou na cestě a fotografie byly poskytnuty dodavatelem bez možnosti výrobek u nás nafotit. S naskladněním zboží vše kontrolujeme a případné rozdíly napravujeme. Vyhrazujeme  si právo a změnu obsahu stavebnic (součástek) bez vlivu na jejich popsanou funkci.


V. Právo spotřebitele odstoupit od smlouvy

Byla-li smlouva uzavřena pomocí prostředků komunikace na dálku (v internetovém obchodě), má spotřebitel právo dle ustanovení § 53 odst. 7 zákona č. 40/1964 Sb., občanský zákoník od smlouvy odstoupit do 14 dnů od převzetí plnění (dodání zboží).

Pokud se spotřebitel rozhodne využít tohoto práva odstoupit od smlouvy, je nutné splnit následující podmínky:
1.Projevit svoji vůli odstoupit od smlouvy, a to nejlépe formou emailu na emailovou adresu info@cybertronic-labs.cz , kde bude uvedeno: jméno kupujícího, adresa, číslo objednávky (fakturace), číslo účtu, na který bude vrácená částka za zboží převedena. Není vyloučeno učinit projev vůle odstoupit od smlouvy i jiným vhodným způsobem, např. doporučeným dopisem. Odstoupení od smlouvy musí být doručeno nejpozději 14. den od převzetí plnění (dodání zboží).
2.Vrácené zboží nesmí být použité!
3.Vrácené zboží nesmí být žádným způsobem poškozeno či jinak znehodnocené!
4.Společně s vráceným zbožím musí být zaslán také originální doklad (faktura), jež byl prodávajícím odeslán po vyexpedování objednávky na kupujícím uvedenou emailovou adresu a zároveň byl součástí kupujícím obdržené zásilky.
5.Přepravné spojené se zasláním zboží zpět k prodávajícímu hradí prodávající, vrácené zboží by mělo být při přepravě pojištěno (při ztrátě zboží a nepojištění zásilky odpovídá za vzniklou událost spotřebitel).
6.Zboží nesmí být zasláno na dobírku.
Po doučení vráceného zboží bude kupujícímu vystaven a odeslán do 7 dnů dobropis, který je kupující povinen podepsaný vrátit na adresu prodávajícího. Kupní cena zboží bude prodávajícímu vrácena do 30 dnů od odstoupení od smlouvy. Prodávající má právo pouze na náhradu skutečně vynaložených nákladů spojených s vrácením zboží.


VI. Rozpor s kupní smlouvou


V případě, že zboží při převzetí kupujícím není ve shodě s kupní smlouvou (dále jen „rozpor s kupní smlouvou"), má kupující právo na to, aby prodávající bezplatně a bez zbytečného odkladu zboží uvedl do stavu odpovídajícího kupní smlouvě, a to podle požadavku kupujícího buď výměnou zboží nebo její opravou. Není-li takový postup možný, může kupující požadovat přiměřenou slevu z ceny věci nebo od smlouvy odstoupit. No neplatí, pokud kupující před převzetím zboží o rozporu s kupní smlouvou věděl nebo rozpor s kupní smlouvou sám způsobil. Rozpor s kupní smlouvou, který se projeví během šesti měsíců ode dne převzetí zboží se považuje za rozpor existující již při jejím převzetí, pokud to neodporuje povaze zboží nebo pokud se neprokáže opak.

Shodou s kupní smlouvou se zejména rozumí, že prodávaná věc má jakost a užitné vlastnosti smlouvou požadované, prodávajícím, výrobcem nebo jeho zástupcem popisované, nebo na základě jimi prováděné reklamy očekávané, popřípadě jakost a užitné vlastnosti pro věc takového druhu obvyklé, že odpovídá požadavkům právních předpisů, je v tomu odpovídajícím množství, míře nebo hmotnosti a odpovídá účelu, který prodávající pro použití věci uvádí nebo pro který se věc obvykle používá.


VII. Záruční podmínky


Na dodané zboží se vztahuje záruka 24 měsíců pro běžného spotřebitele a 12 měsíců pro fyzickou či právnickou osobu. Záruční doba počíná běžet dnem převzetí testovaného produktu kupujícím. Záruční doba se prodlužuje o dobu, po kterou bylo zboží v záruční opravě případně k přehrání FW na přání zákazníky (hobby vývojář). V případě výměny zboží za nové v záruční době začíná běžet nová záruka. Záruka se nevztahuje na opotřebení zboží způsobené jeho obvyklým užíváním. Zboží je nutné reklamovat zasláním prodejci, nikoliv na dobírku, s číslem faktury a datem prodeje.
Spotřebitel při uplatnění záruky má:
1. 
1.Jde-li o vadu odstranitelnou, právo na bezplatné, řádné a včasné odstranění vady, právo na výměnu vadného zboží a není-li takový postup možný, právo na přiměřenou slevu z kupní ceny nebo odstoupit o kupní smlouvy
2.Jde-li o vadu neodstranitelnou bránící řádnému užívání zboží, právo na výměnu vadného zboží nebo odstoupit od kupní smlouvy
3.Jde-li o vady odstranitelné vyskytující se ve větším počtu nebo opakovaně a bránící řádnému užívání zboží, právo na výměnu vadného zboží nebo odstoupit od kupní smlouvy
4.Jde-li o jiné vady neodstranitelné a nepožaduje-li výměnu věci, právo na přiměřenou slevu z kupní ceny nebo odstoupit od smlouvy.
Prodávající rozhodne o reklamaci ihned, ve složitějších případech do 3 dnů, do této lhůty se nezapočítává nutná doba pro odborné posouzení reklamované vady. Nejpozději bude reklamace vyřízena včetně odstranění vady do 30 dnů. Tuto lhůtu lze po dohodě se spotřebitelem prodloužit. Po uplynutí této lhůty má spotřebitel stejná práva jako by se jednalo o vadu, kterou nelze odstranit.

Prodávající je povinen vydat spotřebiteli písemné potvrzení o tom, kdy spotřebitel právo uplatnil, co je obsahem reklamace a jaký způsob vyřízení reklamace spotřebitel požaduje, dále potvrzení o datu a způsobu vyřízení reklamace, případně písemné odůvodnění zamítnutí reklamace. Přesně takto je nařízená záruka legislativou ČR. V praxi veškeré nařízení dodržujeme, ovšem i po uplynutí "záručního období" obvykle bastl moduly zdarma opravujeme, testujeme, upravujeme firmware a to z důvodů vývoje. Neboť každý zákazník je u nás považovaný jako hobby vývojář, který se svými nápady, úpravy a připomínky podílí na vývoji finálního produktu, který bude po otestování v akreditované laboratoři v duchu zákona č.22/1997 uveden na trh s patřičnou shodou.


VIII. Ochrana osobních dat

1. Veškeré informace o kupujícím jsou uchovány v souladu s právním řádem České republiky, zejména v souladu se zákonem č. 101/ 2000 Sb. o ochraně osobních údajů, v jeho platném znění. Uzavřením kupní smlouvy kupující souhlasí se zpracováním jeho osobních údajů až do jeho písemného nesouhlasu s tímto zpracováním. Veškeré údaje získané od kupujících slouží výhradně pro vnitřní využití prodávajícího a nejsou poskytovány třetím osobám, mimo kurýrní služby, které jsou osobní údaje kupujícího poskytovány pouze v rozsahu potřebném k dodání objednaného zboží..

2.Osobní údaje, jež jsou poskytnuty v registračním formuláři, slouží výhradně k zprostředkování obchodu mezi kupujícím a prodávajícím. Tímto se prodávající zavazuje, že tyto data nebudou žádným způsobem zneužita nebo poskytnuta třetí osobě Kupující má právo přístupu ke svým osobním údajům a právo na jejich opravu včetně dalších zákonných práv k těmto údajům. Kupující má právo písemně, e-mailem nebo telefonicky vyjádřit nesouhlas s evidencí osobních údajů. V tom případě prodávající odstraní tyto údaje v nezbytném rozsahu ze své databáze.

3.Osobní údaje uchováváme v databází po dobu platnosti smlouvy, nejdele však 1 rok. Vaše osobní údaje jsou u nás v bezpečí. Abychom zamezili neoprávněnému přístupu a zneužití vašich osobních údajů, zavedli jsme přiměřená opatření jak technického, tak organizačního rázu. Vaše osobní údaje využíváme jen pro registraci objendávky a její zaslání k odběrateli.

 


IX. Vyjádření autora (výrobce) v duchu zákona č.22/1997 o technických požadavcích na výrobky:

Výrobce stavebnice (electronic kits) zaručuje správnou a bezchybnou činnost stavebnice po jejím odborném a bezchybném sestavení. Protože se však jedná o hobby stavebnici určenou pro radioamatéry a ne o finální výrobek, nelze převzít jakoukoliv zodpovědnost za škody způsobené špatnou činností zařízení v případě neodborného sestavení, zapojení a provozování za podmínek, které jsou v rozporu s konstrukčním návodem. Stavebnice není, z hlediska bezpečnosti, určená k ovládání zařízení, strojů a přístrojů, které by mohly při špatné funkci této konstrukce způsobit škody na zdraví či majetku lidí! U všech výrobků jsou návody i s tímto upozorněním volně přístupné na stránce výrobce (www.cybertronic-labs.cz), aby měl každý konstruktér možnost se seznámit s technickými daty stavebnice ještě před jejím zakoupením. Na přání konstruktéra (zákazníka) lze stavebnici předat k sestavení nezávislé technické podpoře, která s firmou René Letocha, CYBERTRONIC Unlimited nemá nic společného. Sestavení probíhá zdarma za účelem získání příslušné technické dovednosti, jedná se pouze o smluvní záležitost mezi zákazníkem (konstruktérem) a studentem, který se na stavbě podílí. Na sestavení se podílí studenti středních i vysokých technických škol. Studenti po sestavení provádí funkční test. Firma René Letocha, CYBERTRONIC Unlimited veškeré  moduly prodává a fakturuje pouze jako stavebnice pro hobby konstruktéry!

Dále je nutné brát zřetel na to, že se fyzicky nejedná o zavádění výrobku na trh. Prakticky nejde o finální výrobek, ale stále jde pouze o elektronický "bastl" určený pro kutili a rádioamatéry. Veškeré výrobky jsou nabízeny jako stavebnice, kde na zdokonalení se podílí sám zákazník, co by hobby vývojář. Takový hobby vývojář může mít své připomínky, poznatky, které budou vždy bezplatně zavedeny do výrobky a předány zpět hobby vývojáři. Pokud již potencionální zákazníci co by hobby vývojáři nemají žádné připomínky, putuje výrobek k přetestování v duchu zákona č.22/1997 o technických požadavcích na výrobky. Výrobek pak dostane prohlášení o shodě a zavede se na trh. 

 


X. Elektromagnetická kompatibilita (EMC) v duchu zákona č.616/2006 Sb :

Technické požadavky na výrobky z hlediska jejich elektromagnetické kompatibility, musí splnit veškerá elektronika uváděná na trh. Platí to i pro zařízení pracující s napětím menším než 50 V AC a 75 V DC. (zařízení napájená z baterií , adaptérů apod.) Na všechna elektronická zařízení uváděná na trh musí být provedena zkouška EMC. Zkouška EMC nemusí být provedena pouze na elektronické stavebnice, které nejsou běžně dostupné na trhu a nejsou samostatně funkční - bastly radioamatérů, nedokončené prototypové výrobky apod.

 


XI. Závěrečná ustanovení

1. Veškeré vyobrazené zboží a veškeré fotografie doplněné copyrightem CYBERTRONIC Unlimited jsou vlastnictvím společnosti René Letocha, CYBERTRONIC Unlimited, IČ: 75685761, DIČ: (nejme plátci DPH).
2.Tyto všeobecné obchodní podmínky vstupují v platnost dne 13.4.2018.</p>
        </div>
      );
    } else if (i18n.language === 'sk') {
      return (
        <div className="space-y-4 text-sm leading-relaxed">
          <p><strong>I. Úvodné ustanovenie</strong></p>
          <p>Tieto záväzné obchodné podmienky platia pre nákup v internetovom obchode cybertronic-labs.cz. Tieto obchodné podmienky upravujú vzájomný obchodný vzťah medzi predávajúcim, ktorým je prevádzkovateľ tohto internetového obchodu a kupujúcim (zákazníkom). Všetky obchodné vzťahy sú uzavreté v súlade s právnym poriadkom Českej republiky.</p>

          <p><strong>II. Platobné a dodacie podmienky</strong></p>
          <p>Platba je možná iba bankovým prevodom. Číslo účtu: 1352505023/0800 (ČESKÁ SPOŘITELNA), Zahraničie (IBAN): CZ30 0800 0000 0013 5250 5023, BIC: GIBA CZ PX. Výroba trvá v závislosti na vyťaženosti v intervale 1-20 pracovných dní.</p>

          <p><strong>III. Právo spotrebiteľa odstúpiť od zmluvy</strong></p>
          <p>Spotrebiteľ má právo odstúpiť od zmluvy do 14 dní od prevzatia plnenia. Vrátený tovar nesmie byť použitý ani poškodený.</p>

          <p><strong>IV. Záručné podmienky</strong></p>
          <p>Na dodaný tovar sa vzťahuje záruka 24 mesiacov pre bežného spotrebiteľa a 12 mesiacov pre fyzickú či právnickú osobu.</p>

          <p><strong>V. Ochrana osobných údajov</strong></p>
          <p>Všetky informácie o kupujúcom sú uchované v súlade s právnym poriadkom Českej republiky, najmä v súlade so zákonom č. 101/2000 Zb. o ochrane osobných údajov.</p>

          <p className="mt-6"><strong>Tieto všeobecné obchodné podmienky vstupujú v platnosť dňa 13.4.2018.</strong></p>
        </div>
      );
    } else if (i18n.language === 'de') {
      return (
        <div className="space-y-4 text-sm leading-relaxed">
          <p><strong>I. Einleitende Bestimmungen</strong></p>
          <p>Diese verbindlichen Geschäftsbedingungen gelten für den Einkauf im Online-Shop cybertronic-labs.cz. Diese Geschäftsbedingungen regeln die gegenseitigen Handelsbeziehungen zwischen dem Verkäufer, dem Betreiber dieses Online-Shops, und dem Käufer (Kunden). Alle Handelsbeziehungen werden im Einklang mit der Rechtsordnung der Tschechischen Republik geschlossen.</p>

          <p><strong>II. Zahlungs- und Lieferbedingungen</strong></p>
          <p>Die Zahlung ist nur per Banküberweisung möglich. Kontonummer: 1352505023/0800 (ČESKÁ SPOŘITELNA), Ausland (IBAN): CZ30 0800 0000 0013 5250 5023, BIC: GIBA CZ PX. Die Produktion dauert je nach Auslastung 1-20 Arbeitstage.</p>

          <p><strong>III. Widerrufsrecht des Verbrauchers</strong></p>
          <p>Der Verbraucher hat das Recht, innerhalb von 14 Tagen nach Erhalt der Ware vom Vertrag zurückzutreten. Die zurückgegebene Ware darf weder benutzt noch beschädigt sein.</p>

          <p><strong>IV. Garantiebedingungen</strong></p>
          <p>Für die gelieferte Ware gilt eine Garantie von 24 Monaten für den normalen Verbraucher und 12 Monaten für natürliche oder juristische Personen.</p>

          <p><strong>V. Datenschutz</strong></p>
          <p>Alle Informationen über den Käufer werden im Einklang mit der Rechtsordnung der Tschechischen Republik aufbewahrt, insbesondere im Einklang mit dem Gesetz Nr. 101/2000 Sb. über den Schutz personenbezogener Daten.</p>

          <p className="mt-6"><strong>Diese allgemeinen Geschäftsbedingungen treten am 13.4.2018 in Kraft.</strong></p>
        </div>
      );
    } else {
      return (
        <div className="space-y-4 text-sm leading-relaxed">
          <p><strong>I. Introductory Provisions</strong></p>
          <p>These binding business conditions apply to purchases in the cybertronic-labs.cz online store. These business conditions regulate the mutual business relationship between the seller, who is the operator of this online store, and the buyer (customer). All business relationships are concluded in accordance with the legal order of the Czech Republic.</p>

          <p><strong>II. Payment and Delivery Conditions</strong></p>
          <p>Payment is only possible by bank transfer. Account number: 1352505023/0800 (ČESKÁ SPOŘITELNA), Foreign (IBAN): CZ30 0800 0000 0013 5250 5023, BIC: GIBA CZ PX. Production takes 1-20 working days depending on capacity.</p>

          <p><strong>III. Consumer's Right to Withdraw from the Contract</strong></p>
          <p>The consumer has the right to withdraw from the contract within 14 days of receiving the goods. The returned goods must not be used or damaged.</p>

          <p><strong>IV. Warranty Conditions</strong></p>
          <p>The delivered goods are covered by a warranty of 24 months for regular consumers and 12 months for natural or legal persons.</p>

          <p><strong>V. Personal Data Protection</strong></p>
          <p>All information about the buyer is stored in accordance with the legal order of the Czech Republic, especially in accordance with Act No. 101/2000 Coll. on the protection of personal data.</p>

          <p className="mt-6"><strong>These general terms and conditions come into effect on April 13, 2018.</strong></p>
        </div>
      );
    }
  };

  // Funkce pro pozdrav podle denní doby
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 18) {
      return t('cart.goodDay');
    } else {
      return t('cart.goodEvening');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20">
      <ParticleBackground />
      <section className="py-20 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* MODAL obchodní podmínky */}
          {showTerms && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
              <div className="bg-gray-900 text-gray-200 rounded-2xl p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto relative">
                <button onClick={() => setShowTerms(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
                <h2 className="text-2xl font-bold mb-4">{t('cart.terms')}</h2>
                {getTranslatedTerms()}
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center mb-6">
              <ShoppingCart className="w-16 h-16 text-[#EE1C23] mr-4" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron">
                {t('shop.cart')}
              </h1>
            </div>
          </motion.div>

          {isLoading ? (
            <div className="text-center py-20">
              <div className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-12 max-w-md mx-auto">
                <div className="animate-spin w-16 h-16 border-4 border-[#EE1C23] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-400 text-lg">
                  Načítání košíku...
                </p>
              </div>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-12 max-w-md mx-auto">
                <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-4">
                  {t('shop.emptyCart')}
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    window.location.href = '/shop';
                  }}
                  className="bg-gradient-to-r from-[#EE1C23] to-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all"
                >
                  {t('shop.title')}
                </motion.button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-black/40 backdrop-blur-md border rounded-2xl p-6"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-20 h-20 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover"/>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-2">{item.name}</h3>
                        <div className="flex items-center gap-3">
                          <p className="text-[#EE1C23] font-bold">{formatPrice(getItemPriceWithVat(item), i18n.language)}</p>
                          {item.category !== 'unlimited' && (
                            <button
                              onClick={() => toggleVat(item.id)}
                              className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                isVatOn(item)
                                  ? 'bg-green-600 text-white'
                                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              {isVatOn(item) ? 's DPH' : 'bez DPH'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 bg-gray-700 hover:bg-gray-600 text-white rounded-full flex items-center justify-center transition-colors"><Minus className="w-4 h-4" /></button>
                        <span className="text-white font-semibold w-8 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 bg-gray-700 hover:bg-gray-600 text-white rounded-full flex items-center justify-center transition-colors"><Plus className="w-4 h-4" /></button>
                        <button onClick={() => removeItem(item.id)} className="w-8 h-8 bg-red-600 hover:bg-red-500 text-white rounded-full flex items-center justify-center transition-colors ml-4"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Order Summary + Checkout */}
              <div className="space-y-6">
                <div className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4">{t('shop.orderSummary')}</h3>
                  <div className="space-y-3 mb-6">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex justify-between text-gray-300">
                        <span>{item.quantity}x {item.name}{item.category !== 'unlimited' && <span className="text-gray-500 text-xs ml-1">({isVatOn(item) ? 's DPH' : 'bez DPH'})</span>}</span>
                        <span>{formatPrice(getItemPriceWithVat(item) * item.quantity, i18n.language)}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Shipping Options */}
                  <div className="border-t border-gray-600 pt-4 mb-4">
                    <h4 className="text-lg font-semibold text-white mb-3">{t('shop.shippingType')}</h4>
                    <div className="space-y-4">
                      {shippingOptions.map((option) => (
                        <div key={option.id} className="p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                          <label className="flex items-start space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name="shipping"
                              value={option.id}
                              checked={selectedShipping === option.id}
                              onChange={handleShippingChange}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <span className="text-white font-medium">
                                  {i18n.language === 'cz' || i18n.language === 'cs' ? option.name :
                                   i18n.language === 'de' ? option.nameDe :
                                   i18n.language === 'sk' ? option.nameSk :
                                   option.nameEn}
                                </span>
                                <span className="text-[#EE1C23] font-bold whitespace-nowrap ml-2">
                                  {formatShippingPrice(option.price)}
                                </span>
                              </div>
                              <p className="text-gray-400 text-xs mt-1 mb-2">
                                {i18n.language === 'cz' || i18n.language === 'cs' ? option.deliveryTime :
                                 i18n.language === 'de' ? option.deliveryTimeDe :
                                 i18n.language === 'sk' ? option.deliveryTimeSk :
                                 option.deliveryTimeEn}
                              </p>
                              <p className="text-gray-400 text-xs leading-relaxed">
                                {i18n.language === 'cz' || i18n.language === 'cs' ? option.description :
                                 i18n.language === 'de' ? option.descriptionDe :
                                 i18n.language === 'sk' ? option.descriptionSk :
                                 option.descriptionEn}
                              </p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-600 pt-4 mb-6 space-y-2">
                    <div className="flex justify-between text-gray-300">
                      <span>{t('shop.subtotal')}:</span>
                      <span>{formatPrice(getTotalPrice(), i18n.language)}</span>
                    </div>
                    <div className="flex justify-between text-gray-300">
                      <span>{t('shop.shipping')}:</span>
                      <span>{formatPrice(getShippingPriceInCZK(), i18n.language)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold text-white pt-2 border-t border-gray-600">
                      <span>{t('shop.total')}:</span>
                      <span className="text-[#EE1C23]">{formatPrice(getFinalPrice(), i18n.language)}</span>
                    </div>
                  </div>
                  
                  {!showForm && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleOrderClick}
                      className="w-full bg-gradient-to-r from-[#EE1C23] to-red-600 text-white py-3 rounded-lg font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all flex items-center justify-center space-x-2"
                    >
                      <Package className="w-5 h-5" />
                      <span>{t('shop.order')}</span>
                    </motion.button>
                  )}
                </div>

                {/* Checkout Form */}
                {showForm && (
                  <form onSubmit={handleSubmit} className="bg-black/60 backdrop-blur-md border border-green-500/20 rounded-2xl p-6 space-y-4">
                    <h3 className="text-lg font-bold text-white mb-2">{t('shop.fillDeliveryInfo')}</h3>
                    
                    <input className="w-full p-2 rounded bg-gray-800 text-white" type="text" name="name" placeholder={t('shop.nameCompany')} value={formData.name} onChange={handleChange} required />
                    <input className="w-full p-2 rounded bg-gray-800 text-white" type="email" name="email" placeholder={t('contact.email')} value={formData.email} onChange={handleChange} required />
                    <input className="w-full p-2 rounded bg-gray-800 text-white" type="tel" name="phone" placeholder={t('contact.phone')} value={formData.phone} onChange={handleChange} required />
                    <input className="w-full p-2 rounded bg-gray-800 text-white" type="text" name="billingAddress" placeholder={t('shop.billingAddress')} value={formData.billingAddress} onChange={handleChange} required />
                    <input className="w-full p-2 rounded bg-gray-800 text-white" type="text" name="shippingAddress" placeholder={t('shop.shippingAddress')} value={formData.shippingAddress} onChange={handleChange} />
                    
                    <div className="space-y-2">
                      <p className="text-gray-400 text-xs">{t('shop.gateDimensionsHint')}</p>
                      <div className="flex space-x-2">
                        <input className="w-1/2 p-2 rounded bg-gray-800 text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" type="number" name="gateWidth" placeholder={t('shop.gateWidth')} value={formData.gateWidth} onChange={handleChange} />
                        <input className="w-1/2 p-2 rounded bg-gray-800 text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" type="number" name="gateHeight" placeholder={t('shop.gateHeight')} value={formData.gateHeight} onChange={handleChange} />
                      </div>
                    </div>
                    
                    <input className="w-full p-2 rounded bg-gray-800 text-white" type="text" name="workHours" placeholder={t('shop.workHours')} value={formData.workHours} onChange={handleChange} required />
                    <textarea className="w-full p-2 rounded bg-gray-800 text-white" name="notes" placeholder={t('shop.notes')} value={formData.notes} onChange={handleChange}></textarea>
                    
                    <label className="flex items-center space-x-2 text-gray-300 text-sm">
                      <input type="checkbox" name="consent" checked={formData.consent} onChange={handleChange} required />
                      <span>
                        {t('shop.agreeTerms')}{' '}
                        <button type="button" onClick={() => setShowTerms(true)} className="underline text-blue-400 hover:text-blue-300">
                          {t('shop.termsConditions')}
                        </button>
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white py-3 rounded-lg font-semibold hover:from-green-500 hover:to-green-600 transition-all disabled:opacity-50"
                    >
                      {isSubmitting ? t('shop.sending') : t('shop.sendOrder')}
                    </button>
                  </form>
                )}

                <div className="bg-black/40 backdrop-blur-md border border-blue-500/20 rounded-2xl p-6 text-gray-300 text-sm space-y-3">
                  <p><strong>{getGreeting()}</strong></p>
                  <p>{t('cart.privacyInfo')}</p>
                  <p className="text-right"><strong>{t('cart.thankYou')}<br/>Letocha René</strong></p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <AnimatePresence mode="wait">
        {showSuccessModal && (
          <OrderSuccessModal
            key="order-success"
            orderNumber={completedOrderNumber}
            onClose={() => setShowSuccessModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}